import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Preferences } from '@capacitor/preferences'
import {
  authentication,
  createDirectus,
  createFolder,
  createItem,
  createItems,
  createNotification,
  createUser,
  createUsers,
  deleteFiles,
  deleteFolders,
  deleteItem,
  deleteItems,
  deleteNotification,
  deleteNotifications,
  passwordRequest,
  passwordReset,
  readFiles,
  readFlows,
  readFolders,
  readItem,
  readItems,
  readMe,
  readNotifications,
  readRoles,
  readUser,
  readUsers,
  rest,
  triggerFlow,
  updateFile,
  updateItem,
  updateItems,
  updateMe,
  updateNotifications,
  updateUser,
  updateUsers,
  uploadFiles
} from '@directus/sdk'
import isEmpty from 'lodash-es/isEmpty'
import { BehaviorSubject } from 'rxjs'
import { v4 as uuidv4 } from 'uuid'
import { environment } from '../environments/environment'
import {
  Achievement,
  Collectable,
  Dictionary,
  DirectusRoles,
  DirectusUsers,
  EnglishTerm,
  EnglishTermTerm,
  Game,
  GameEnglishTerm,
  GameFiles,
  GameMap,
  GroupDirectusUsers1,
  JunctionDirectusUsersDictionary,
  LearningEvent,
  Lesson,
  LessonGame,
  Level,
  Organisation
} from '../types'
import {
  DictionaryStatus,
  GameMapStatus,
  ManagementLevel,
  UsersDictionaryCollection
} from './content-management/content-management-model'
import { GameType, LearningEventType, SideActivityType } from './game/game-model'
import { Webhook, WebhookMethod } from './model/general'
import { DIRECTUS_STORAGE, ITokenData, TokenData, UserRole } from './model/user-model'
import { GroupMemberCollection } from './my-students/my-students-model'
import { GlobalErrorHandler } from './services/global-error-handler/global-error-handler.service'
import { DBCollection, RxdbService } from './services/rxdb/rxdb.service'

export type ApiError = Error & {
  errors?: Array<{
    extensions?: {
      code: string | 'INVALID_CREDENTIALS'
    }
    message: string
  }>
}

export const authLocalStorage = (mainKey: string = DIRECTUS_STORAGE) => ({
  get: async () => {
    const data: string | null = (await Preferences.get({ key: mainKey })).value
    if (data) {
      return JSON.parse(data)
    }
    return null
  },
  set: async (value: { [k: string]: string | number | null }) => {
    if (!value) {
      await Preferences.remove({ key: mainKey })
      return
    }

    await Preferences.set({
      key: mainKey,
      value: JSON.stringify(value)
    })
  },
  clear: async () => {
    await Preferences.clear()
  }
})

@Injectable({
  providedIn: 'root'
})
export class BackendService {
  private _token$ = new BehaviorSubject<string | null>(null)
  token$ = this._token$.asObservable()

  directus = createDirectus(environment.api)
    .with(authentication('json', { storage: authLocalStorage() as any }))
    .with(rest())

  constructor(
    private router: Router,
    private rxdbService: RxdbService,
    private globalErrorHandler: GlobalErrorHandler
  ) {
    this.getToken()
  }

  //#region User and Auth
  get token() {
    return this._token$.getValue()
  }

  async getUserAccount(id: string) {
    const result = await this.directus.request(
      readUser(id, {
        fields: ['id', 'confirmed']
      })
    )

    return result
  }

  async autoLogin() {
    const cache = await authLocalStorage().get()
    if (!cache) return

    await this.refreshToken()
    return this.getLoggedUser()
  }

  async extractTokenDataFromLocalStorage(): Promise<ITokenData | null> {
    const directusStorage = (await Preferences.get({ key: DIRECTUS_STORAGE })).value

    return directusStorage ? JSON.parse(directusStorage) : null
  }

  async refreshToken() {
    try {
      const tokenData = await this.extractTokenDataFromLocalStorage()
      if (tokenData?.[TokenData.ExpiresAt] && Date.now() < tokenData?.[TokenData.ExpiresAt]) {
        await this.directus.refresh()
        await this.getToken()
        return true
      }
      return false
    } catch (err) {
      this.router.navigateByUrl('/')
      return false
    }
  }

  async getToken() {
    const token = await this.directus.getToken()
    this._token$.next(token)
  }

  async getLoggedUser() {
    if (!this.token) return null

    try {
      const user = await this.directus.request(
        readMe({
          fields: [
            '*',
            'role.*',
            {
              dictionaries: ['status', 'dictionary_id.*']
            },
            'default_dictionary.*',
            {
              default_organisation: [
                'id',
                'name',
                'isLanguageProvider',
                { owned_dictionaries: ['dictionary_id.*', 'dictionary_id.organisation.organisation_id'] },
                { used_dictionaries: ['dictionary_id.*', 'dictionary_id.organisation.organisation_id'] }
              ]
            },
            {
              organisations: [
                {
                  organisation_id: [
                    'id',
                    'name',
                    'isLanguageProvider',
                    { owned_dictionaries: ['dictionary_id.*', 'dictionary_id.organisation.organisation_id'] },
                    { used_dictionaries: ['dictionary_id.*', 'dictionary_id.organisation.organisation_id'] }
                  ]
                }
              ]
            }
          ]
        })
      )

      if (!user.confirmed) return null

      return user
    } catch (err) {
      this.router.navigateByUrl('/')

      return null
    }
  }

  getRoles(role: string | null = null) {
    const filter = role ? { name: { _eq: role } } : {}

    return this.directus.request<DirectusRoles[]>(
      readRoles({
        filter,
        fields: ['id', 'name']
      })
    )
  }

  async confirmUserAccount(id: string) {
    try {
      await this.directus.request(updateUser(id, { confirmed: true } as any))
    } catch (err) {
      console.error(err)
    }
  }

  async createNewUser(user: Partial<DirectusUsers>, userRole: string) {
    try {
      const rolesList = await this.getRoles()

      const role: DirectusRoles | undefined = rolesList.find((r) => r.name === userRole)

      if (role) {
        const createdUser = await this.createUser({
          ...user,
          confirmed: false,
          id: uuidv4(),
          role
        } as any)

        return createdUser
      } else {
        throw new Error(`Role ${userRole} is not exist`)
      }
    } catch (err) {
      this.globalErrorHandler.handleError(err)

      return null
    }
  }

  createUser(user: any) {
    return this.directus.request(createUser(user))
  }

  createBulkUsers(users: any) {
    return this.directus.request(createUsers(users))
  }

  async updateLoggedUser(user: Partial<DirectusUsers>) {
    return await this.directus.request(updateMe(user as any))
  }

  getUserByEmail(emails: string[]) {
    return this.directus.request(
      readUsers({
        fields: ['*', 'role.*'],
        filter: { email: { _in: emails } },
        limit: -1
      })
    )
  }

  getOrgAdminUsers(orgIds: string[], roles: string[]) {
    return this.directus.request(
      readUsers({
        fields: ['email', 'organisations.*'],
        filter: {
          organisations: {
            organisation_id: { _in: orgIds }
          },
          role: { _in: roles }
        },
        limit: -1
      })
    )
  }

  async login(email: string, password: string) {
    try {
      await this.directus.login(email, password)
      await this.getToken()
      return this.getLoggedUser()
    } catch (err) {
      this.globalErrorHandler.handleError(err)
      return null
    }
  }

  async logout() {
    this._token$.next(null)
    await this.directus.logout()
    await authLocalStorage().clear()
  }

  async resetPasswordRequest(email: string) {
    await this.directus.request(passwordRequest(email, `${environment.url}/change-password`))
  }

  async resetPassword(resetToken: string, newPassword: string) {
    await this.directus.request(passwordReset(resetToken, newPassword))
  }

  updateStudentPasswords(studentIds: string[], password: string) {
    return this.directus.request(updateUsers(studentIds, { password }))
  }

  getOrgById(id: string) {
    return this.directus.request(readItem('organisation', id))
  }

  //#endregion User and Auth

  //#region Learning events
  get localLearningEventsCollection() {
    return this.rxdbService.getDatabaseCollection(DBCollection.LearningEvents)
  }

  private addIdToLearningEvent(learningEvent: Partial<LearningEvent>): LearningEvent {
    return {
      id: uuidv4(),
      ...learningEvent
    }
  }

  addLearningEventToRxdb(learningEvent: Omit<LearningEvent, 'id'>) {
    this.rxdbService.create(this.localLearningEventsCollection, {
      date_created: new Date().toISOString(),
      ...this.addIdToLearningEvent(learningEvent)
    })
  }

  addLearningEvent(learningEvent: Omit<LearningEvent, 'id'>) {
    const updatedLearningEvent = this.addIdToLearningEvent(learningEvent)

    return this.directus.request(createItem('learning_event', updatedLearningEvent))
  }

  addLearningEvents = (learningEvent: Partial<LearningEvent>[]) =>
    this.directus.request(createItems('learning_event', learningEvent))

  async getLearningEventsByType(type: LearningEventType, dictionaryId: string | undefined | null = '') {
    const filter: { [k: string]: any } = { type: { _eq: type } }
    if (dictionaryId) filter.dictionary = { _eq: dictionaryId }

    await this.syncLearningEventsData()

    return this.directus.request(
      readItems('learning_event', {
        fields: ['*'],
        filter,
        limit: -1
      })
    )
  }

  getAllLearningEventsForCurrentUser() {
    return this.directus.request(
      readItems('learning_event', {
        fields: ['*'],
        limit: -1
      })
    )
  }

  async syncLearningEventsData() {
    const data = await this.rxdbService.getList(this.localLearningEventsCollection)
    if (!data || isEmpty(data)) return

    const remoteLearningEventsCollection: { [data: string]: any } = await this.directus.request(
      readItems('learning_event', {
        fields: ['*'],
        limit: 100,
        sort: ['-date_created']
      })
    )

    const remoteLearningEvents: any[] = remoteLearningEventsCollection?.data ?? []

    for (const { _data } of data) {
      const includes = remoteLearningEvents.some((e: LearningEvent) => e.id === _data.id)
      if (includes) return

      await this.directus.request(createItem('learning_event', _data))
    }

    await this.rxdbService.clearCollection(this.localLearningEventsCollection)
    await this.rxdbService.createCollections()
  }

  getStudentsEvents(
    types: LearningEventType[],
    createdIds: string[] | null,
    assignedIds: string[] | null
  ): Promise<{ [k: string]: any }> {
    const filter: { [k: string]: any } = {
      type: { _in: types }
    }

    if (createdIds && !isEmpty(createdIds)) filter['user_created'] = { _in: createdIds }
    if (assignedIds && !isEmpty(assignedIds)) filter['user_assigned'] = { _in: assignedIds }

    return this.directus.request(
      readItems('learning_event', {
        fields: ['*'],
        filter: filter,
        limit: -1
      })
    )
  }

  async deleteLearningEventsByUser() {
    const learningEvents: any = await this.getAllLearningEventsForCurrentUser()
    const ids: string[] = learningEvents.map((e: LearningEvent) => e.id)

    if (isEmpty(ids)) return

    return this.directus.request(deleteItems('learning_event', ids))
  }
  //#endregion Learning events

  //#region Group member
  addGroupMember(collection: string, member: any) {
    return this.directus.request(createItem(collection, member))
  }

  getUserDictionaryRecords(dictionaryIds: string[], userId: string) {
    return this.directus.request(
      readItems(UsersDictionaryCollection, {
        fields: ['*'],
        filter: {
          dictionary_id: { _in: dictionaryIds },
          directus_users_id: { _eq: userId }
        },
        limit: -1
      })
    )
  }

  addBulkGroupMembers(collection: string, members: any) {
    return this.directus.request(createItems(collection, members))
  }

  updateGroupPendingMember(id: string | number, pendingMemberItem: Partial<GroupDirectusUsers1>) {
    return this.directus.request(updateItem(GroupMemberCollection.PendingMember, id, pendingMemberItem))
  }

  deleteGroupPendingMembers(groupId: string, memberIds: string[]) {
    return this.directus.request(
      deleteItems(GroupMemberCollection.PendingMember, {
        filter: {
          group_id: { _eq: groupId },
          directus_users_id: { _in: memberIds }
        }
      })
    )
  }

  getMemberGroups(collection: string, memberId: string) {
    return this.directus.request(
      readItems(collection, {
        filter: {
          directus_users_id: { _eq: memberId }
        },
        fields: [
          '*',
          {
            group_id: ['id', 'name', 'organisation.id']
          }
        ]
      })
    )
  }

  deleteMemberGroups(collection: string, memberIds: string[], ids: string[]) {
    const filter: { [k: string]: any } = {
      directus_users_id: { _in: memberIds }
    }

    if (ids.length) filter.group_id = { _in: ids }

    return this.directus.request(deleteItems(collection, { filter }))
  }

  getClasses(orgId: string): Promise<{ [key: string]: any }> {
    const organisation = orgId ? { _eq: orgId } : { _null: true }

    return this.directus.request(
      readItems('group', {
        fields: [
          'id',
          'name',
          {
            members: [
              {
                directus_users_id: ['id', 'role']
              }
            ]
          }
        ],
        filter: {
          organisation
        },
        limit: -1,
        deep: { members: { _limit: -1 } }
      })
    )
  }

  addAClass(_class: any) {
    return this.directus.request(createItem('group', _class))
  }

  deleteAClass(classId: string) {
    return this.directus.request(deleteItem('group', classId))
  }

  async editClassName(id: string, name: string) {
    try {
      const added = await this.directus.request(updateItem('group', id, { name }))
      return added
    } catch (error) {
      this.globalErrorHandler.handleError('Failed to change the class name. Please try again later.')
      return null
    }
  }

  getStudents(classId: string) {
    return this.directus.request(
      readItem('group', classId, {
        fields: [
          {
            members: [
              {
                directus_users_id: [
                  'id',
                  'first_name',
                  'last_name',
                  'avatar',
                  'email',
                  { dictionaries: [{ dictionary_id: ['id', 'name'] }, 'status'] }
                ]
              }
            ]
          },
          {
            pending_members: [
              {
                directus_users_id: [
                  'id',
                  'first_name',
                  'last_name',
                  'avatar',
                  'email',
                  { dictionaries: [{ dictionary_id: ['id', 'name'] }, 'status'] }
                ]
              }
            ]
          }
        ],
        deep: { members: { _limit: -1 }, pending_members: { _limit: -1 } }
      })
    )
  }
  //#endregion Group member

  //#region Flow, email and notification
  async getFlow(flowName: string) {
    const filter = { name: { _eq: flowName } }
    const flow = await this.directus.request(
      readFlows({
        filter,
        fields: ['id', 'name']
      })
    )

    return flow
  }

  async sendEmail(name: Webhook, method: WebhookMethod, payload: any) {
    const flow = await this.getFlow(name)

    this.directus.request(triggerFlow(method, flow[0].id, { ...payload }))
  }

  getNotifications(userId: string) {
    return this.directus.request(
      readNotifications({
        filter: {
          recipient: { _eq: userId }
        },
        sort: ['-timestamp'],
        limit: -1
      })
    )
  }

  createNotification(notification: any) {
    return this.directus.request(createNotification(notification))
  }

  updateNotificationsById(ids: string[], partialNotification: any) {
    return this.directus.request(updateNotifications(ids, partialNotification))
  }

  async removeNotificationsById(ids: string[]) {
    await this.directus.request(deleteNotifications(ids))
  }

  async removeNotification(id: string | number) {
    await this.directus.request(deleteNotification(String(id)))
  }
  //#endregion Flow, email and notification

  //#region Dictionary
  createDictionaryOrganisation(organisationId: string, dictionaryId: string) {
    return this.directus.request(
      createItem('dictionary_organisation', {
        organisation_id: organisationId,
        dictionary_id: dictionaryId
      })
    )
  }

  createOrganisationDictionary(organisationId: string, dictionaryId: string) {
    return this.directus.request(
      createItem('organisation_dictionary_1', {
        organisation_id: organisationId,
        dictionary_id: dictionaryId
      })
    )
  }

  getDictionaryUsers(dictionaryIds: string[], organisation: Organisation) {
    const filter: { [k: string]: any } = {
      dictionary_id: { _in: dictionaryIds }
    }

    filter.directus_users_id = organisation.isLanguageProvider
      ? {
          _or: [
            { 'count(organisations)': { _eq: 0 } },
            { organisations: { organisation_id: { _eq: organisation.id } } }
          ]
        }
      : { organisations: { organisation_id: { _eq: organisation.id } } }

    return this.directus.request(
      readItems(UsersDictionaryCollection, {
        fields: [
          'id',
          'status',
          { dictionary_id: ['id', 'name'] },
          { directus_users_id: ['id', 'first_name', 'last_name', 'role.name', 'email', 'avatar'] }
        ],
        filter,
        limit: -1
      })
    )
  }

  updateDictionaryUsers(ids: string[], user: Partial<JunctionDirectusUsersDictionary>) {
    return this.directus.request(updateItems(UsersDictionaryCollection, ids, user))
  }

  deleteDictionaryUsers(ids: string[]) {
    return this.directus.request(deleteItems(UsersDictionaryCollection, { filter: { id: { _in: ids } } }))
  }

  getCollectedWord(itemId: string) {
    return this.directus.request(
      readItems('learning_event', {
        fields: ['id'],
        filter: {
          type: { _eq: LearningEventType.WordCollected },
          itemId: { _eq: itemId }
        },
        limit: -1
      })
    )
  }

  createDictionary(dictionary: any) {
    return this.directus.request(createItem('dictionary', dictionary))
  }

  getDictionaries() {
    return this.directus.request(
      readItems('dictionary', {
        fields: ['*', 'organisation.organisation_id'],
        limit: -1
      })
    )
  }

  deleteDictionaries(ids: string[]) {
    return this.directus.request(deleteItems('dictionary', ids))
  }

  getDictionaryById(id: string) {
    return this.directus.request(readItem('dictionary', id))
  }

  updateDictionary(dictionary: Partial<Dictionary>) {
    return this.directus.request(updateItem('dictionary', dictionary.id as string, dictionary))
  }

  getDictionaryTerms(dicIds: string[]) {
    return this.directus.request(
      readItems('term', {
        fields: [
          'id',
          'sound.*',
          'original',
          'translated',
          'isSentence',
          'image_mono.*',
          'image_coloured.*',
          'dictionary'
        ],
        filter: {
          dictionary: { _in: dicIds }
        },
        limit: -1
      })
    )
  }

  deleteTerms(ids: string[]) {
    return this.directus.request(deleteItems('term', ids))
  }

  addTerm(term: any) {
    return this.directus.request(createItem('term', term))
  }

  editTerm(term: any) {
    return this.directus.request(updateItem('term', term.id, term))
  }

  addEnglishTerm(term: Partial<EnglishTerm>) {
    return this.directus.request(createItem('English_term', term))
  }

  getEnglishTermByPhrase(phrase: string) {
    return this.directus.request(
      readItems('English_term', {
        fields: ['id'],
        filter: {
          _and: [{ phrase: { _iends_with: phrase } }, { phrase: { _istarts_with: phrase } }]
        }
      })
    )
  }

  async getEnglishTerms() {
    const limit = 500
    const parallelRequests = 4
    let allRecords: EnglishTerm[] = []
    let lastCreatedAt = ''

    const initialBatch = (await this.getTimestampBasedEnglishTerms(limit, lastCreatedAt)) as EnglishTerm[]
    if (initialBatch.length === 0) return allRecords

    allRecords = allRecords.concat(initialBatch)
    lastCreatedAt = initialBatch[initialBatch.length - 1].date_created as string

    while (true) {
      const requests = Array.from({ length: parallelRequests }, (_, i) => {
        const offset = i * limit
        return this.getTimestampBasedEnglishTerms(limit, lastCreatedAt, offset)
      })

      const results = await Promise.all(requests)
      const batchData = results.flat() as EnglishTerm[]

      if (batchData.length === 0) break

      allRecords = allRecords.concat(batchData)
      lastCreatedAt = batchData[batchData.length - 1].date_created as string
    }

    return allRecords
  }

  getTimestampBasedEnglishTerms(limit: number, date_created = '', offset = 0) {
    const filter: { [k: string]: any } = {}

    if (date_created) filter.date_created = { _lt: date_created }

    return this.directus.request(
      readItems('English_term', {
        fields: ['id', 'phrase', 'sound.*', 'terms.term_id.id', 'date_created'],
        sort: ['-date_created'],
        filter,
        limit,
        offset
      })
    )
  }

  addEnglishTermTerm(term: Partial<EnglishTermTerm>) {
    return this.directus.request(createItem('English_term_term', term))
  }

  getEnglishTermTerm(EnglishTermId: string, termId: string) {
    return this.directus.request(
      readItems('English_term_term', {
        filter: {
          English_term_id: { _eq: EnglishTermId },
          term_id: { _eq: termId }
        }
      })
    )
  }

  deleteEnglishTermTerm(id: string) {
    return this.directus.request(deleteItem('English_term_term', id))
  }
  //#endregion

  //#region Game, Files and achievements
  uploadFile(data: FormData) {
    return this.directus.request(uploadFiles(data))
  }

  updateFile(file: any) {
    return this.directus.request(updateFile(file.id as string, file))
  }

  getFolderByNames(folderNames: string[]) {
    const foldersFilter: any = {
      name: {
        _in: folderNames
      }
    }
    return this.directus.request(
      readFolders({
        filter: foldersFilter,
        limit: -1
      })
    )
  }

  async getAllMediaFolderFiles(type = '') {
    const folders: { [k: string]: any } = await this.directus.request(
      readFolders({
        filter: { name: { _contains: '_media' } },
        limit: -1
      })
    )
    const foldersIds = folders.map((f: { id: any }) => f.id)

    const filesFilter: { [k: string]: any } = {
      folder: {
        _in: foldersIds
      }
    }

    if (type) filesFilter.type = { _contains: type }

    if (!filesFilter.folder._in) return

    return await this.directus.request(
      readFiles({
        fields: ['id', 'title', 'filename_disk', 'filename_download', 'folder.*', 'description', 'type'],
        filter: filesFilter,
        limit: -1
      })
    )
  }

  createFolder(folderName: string) {
    return this.directus.request(
      createFolder({
        name: folderName
      })
    )
  }

  deleteFolders(ids: string[]) {
    return this.directus.request(deleteFolders(ids))
  }

  async deleteMediaByFolderIds(folderIds: string[]) {
    const files = await this.directus.request(readFiles({ filter: { folder: { _in: folderIds } } }))
    if (!files.length) return
    return this.directus.request(deleteFiles(files.map((f) => f.id)))
  }

  deleteFiles(ids: string[]) {
    return this.directus.request(deleteFiles(ids))
  }

  async getFilesByFolderNames(folderNames: string[], type = '') {
    const folders: { [k: string]: any } = await this.getFolderByNames(folderNames)
    const foldersIds = folders.map((f: { id: any }) => f.id)

    const filesFilter: { [k: string]: any } = {
      folder: {
        _in: foldersIds
      }
    }

    if (type) filesFilter.type = { _contains: type }

    if (!filesFilter.folder._in) return

    return await this.directus.request(
      readFiles({
        fields: ['id', 'title', 'filename_disk', 'filename_download', 'folder.*', 'description', 'type', 'duration'],
        filter: filesFilter,
        limit: -1
      })
    )
  }

  getGamesWithMedia(fileIds: string[], dictId: string) {
    return this.directus.request(
      readItems('game', {
        fields: ['media'],
        filter: {
          dictionary: { _eq: dictId },
          media: { _in: fileIds }
        },
        limit: -1
      })
    )
  }

  getTermsWithMedia(fileIds: string[], dictId: string) {
    return this.directus.request(
      readItems('term', {
        fields: ['image_coloured', 'image_mono', 'sound'],
        filter: {
          _or: [{ image_coloured: { _in: fileIds } }, { image_mono: { _in: fileIds } }, { sound: { _in: fileIds } }],
          dictionary: { _eq: dictId }
        },
        limit: -1
      })
    )
  }

  getLevelsWithMedia(fileIds: string[], dictId: string) {
    return this.directus.request(
      readItems('level', {
        fields: ['image_coloured', 'image_mono'],
        filter: {
          _or: [{ image_coloured: { _in: fileIds } }, { image_mono: { _in: fileIds } }],
          dictionary: { _eq: dictId }
        },
        limit: -1
      })
    )
  }

  getLessonsWithMedia(fileIds: string[], dictId: string) {
    return this.directus.request(
      readItems('lesson', {
        fields: ['image_coloured', 'image_mono'],
        filter: {
          _or: [{ image_coloured: { _in: fileIds } }, { image_mono: { _in: fileIds } }],
          dictionary: { _eq: dictId }
        },
        limit: -1
      })
    )
  }

  getFileByNames(fileNames: string[]) {
    return this.directus.request(readFiles({ filter: { title: { _in: fileNames } } }))
  }

  getGamesByGameMapId(gameMapId: string) {
    return this.directus.request(
      readItems('game', {
        fields: ['id', 'type', 'unit', 'level', 'subLevel'],
        filter: {
          game_map: { _eq: gameMapId },
          type: { _neq: GameType.SideActivity }
        },
        limit: -1
      })
    )
  }

  getGames(
    dictionaryId: string,
    unit: number,
    level: number,
    subLevel: number | null,
    type: GameType | null,
    gameMapId: string
  ) {
    const filter: { [k: string]: any } = {
      dictionary: { _eq: dictionaryId },
      unit: { _eq: unit },
      level: { _eq: level },
      type: { _neq: GameType.SideActivity },
      game_map: { _eq: gameMapId }
    }

    if (type) filter.type = { _eq: type }
    if (subLevel) filter.subLevel = { _eq: subLevel }

    return this.directus.request(
      readItems('game', {
        fields: [
          'id',
          'data',
          'type',
          'game_map',
          'unit',
          'level',
          'subLevel',
          'media.*',
          { images: ['id', 'directus_files_id.*'] },
          'side_activity_type',
          'side_activity_collectable.*',
          'English_terms.English_term_id.id',
          'answer_English_terms.English_term_id.id'
        ],
        filter: filter,
        limit: -1
      })
    )
  }

  getGameEnglishTermsByDictionaryId(dictionaryId: string) {
    return this.directus.request(
      readItems('game', {
        fields: ['English_terms.English_term_id.id'],
        filter: {
          dictionary: { _eq: dictionaryId },
          type: { _neq: GameType.SideActivity }
        },
        limit: -1
      })
    )
  }

  getGameById(id: string) {
    return this.directus.request(
      readItem('game', id, {
        fields: [
          '*',
          'media.*',
          'side_activity_collectable.*',
          { images: ['id', 'directus_files_id.*'] },
          { game_map: ['id', 'name'] },
          { dictionary: ['*'] },
          'English_terms.English_term_id.id',
          'answer_English_terms.English_term_id.id'
        ]
      })
    )
  }

  createGameEnglishTerms(gameEnglishTerms: Partial<GameEnglishTerm>[]) {
    return this.directus.request(createItems('game_English_term', gameEnglishTerms))
  }

  createGame(game: Partial<Game>) {
    return this.directus.request(createItem('game', game))
  }

  updateGame(game: Partial<Game>) {
    return this.directus.request(updateItem('game', game.id as string, game))
  }

  async updateGames(gameMapId: string, preUnitNumber: number, game: Partial<Game>) {
    const games = await this.directus.request(
      readItems('game', {
        fields: ['id'],
        filter: {
          game_map: { _eq: gameMapId },
          unit: { _eq: preUnitNumber }
        },
        limit: -1
      })
    )
    if (!games.length) return

    return this.directus.request(
      updateItems(
        'game',
        games.map((g) => g.id),
        game
      )
    )
  }

  deleteGame(id: string) {
    return this.directus.request(deleteItem('game', id))
  }

  deleteGamesByUnits(gameMapId: string, levelNumbers: number[]) {
    return this.directus.request(
      deleteItems('game', {
        filter: {
          game_map: { _eq: gameMapId },
          unit: { _in: levelNumbers }
        }
      })
    )
  }

  updateGamesLevelIndex(gameIds: string[], level: number) {
    return this.directus.request(updateItems('game', gameIds, { level }))
  }

  deleteGamesByLevel(gameMapId: string, unit: number, level: number) {
    return this.directus.request(
      deleteItems('game', {
        filter: {
          game_map: { _eq: gameMapId },
          unit: { _eq: unit },
          level: { _eq: level },
          type: { _neq: GameType.SideActivity }
        }
      })
    )
  }

  getGameNumbers() {
    return this.directus.request(
      readItems('game', {
        fields: ['unit', 'level'],
        filter: { type: { _neq: GameType.SideActivity } },
        limit: -1
      })
    )
  }

  getSideActivityCollectablesByIds(ids: string[]) {
    return this.directus.request(
      readItems('game', {
        fields: ['side_activity_collectable.*'],
        filter: {
          id: { _in: ids }
        },
        limit: -1
      })
    )
  }

  getSideActivityCollectables(gameMapId: string) {
    return this.directus.request(
      readItems('game', {
        fields: ['side_activity_collectable.*'],
        filter: {
          type: { _eq: GameType.SideActivity },
          side_activity_type: { _eq: SideActivityType.CollectItem },
          game_map: { _eq: gameMapId }
        },
        limit: -1
      })
    )
  }

  async getAchievements(dictionaryId: string) {
    return await this.directus.request(
      readItems('achievement', {
        fields: ['*', 'image.*', 'image_mono.*'],
        filter: {
          dictionary: { _eq: dictionaryId }
        },
        limit: -1,
        sort: ['title']
      })
    )
  }

  getAchievement(achievementId: string) {
    return this.directus.request(readItem('achievement', achievementId))
  }

  createAchievement(achievement: Partial<Achievement>) {
    return this.directus.request(createItem('achievement', achievement))
  }

  updateAchievement(achievementId: number, achievement: Partial<Achievement>) {
    return this.directus.request(updateItem('achievement', achievementId, achievement))
  }

  deleteAchievement(achievementId: number) {
    return this.directus.request(deleteItem('achievement', achievementId))
  }

  getCollectionCollectables(dictionaryId: string) {
    return this.directus.request(
      readItems('collectable', {
        fields: ['*'],
        filter: {
          _or: [{ dictionary: { _eq: dictionaryId } }, { dictionary: { _null: true } }],
          isCollection: { _eq: true }
        },
        limit: -1,
        sort: ['title']
      })
    )
  }

  createCollectable(collectable: Partial<Collectable>) {
    return this.directus.request(createItem('collectable', collectable))
  }

  updateCollectables(collectableIds: number[], collectable: Partial<Collectable>) {
    return this.directus.request(updateItems('collectable', collectableIds, collectable))
  }

  deleteCollectable(collectableId: number) {
    return this.directus.request(deleteItem('collectable', collectableId))
  }

  deleteCollectables(collectableId: number) {
    return this.directus.request(
      deleteItems('collectable', {
        filter: {
          _or: [{ id: { _eq: collectableId } }, { parent_id: { _eq: collectableId } }]
        }
      })
    )
  }

  getCollectable(collectableId: number) {
    return this.directus.request(readItem('collectable', collectableId, { fields: ['*'] }))
  }

  getChildCollectables(collectableId: number) {
    return this.directus.request(
      readItems('collectable', {
        fields: ['*'],
        filter: {
          parent_id: { _eq: collectableId }
        },
        limit: -1
      })
    )
  }

  async getGameMap(dictionaryId: string, userRole: string, mapId?: string) {
    const filter: { [k: string]: any } = {
      parentmap: { _null: true },
      dictionary: { _eq: dictionaryId }
    }

    if (mapId) filter.id = { _eq: mapId }
    if (userRole !== UserRole.ContentManager) filter.status = { _eq: DictionaryStatus.Published }

    return await this.directus.request(
      readItems('game_map', {
        fields: [
          'dictionary',
          'id',
          'name',
          'date_updated',
          'date_created',
          'status',
          {
            timeline: [
              '*',
              'level.*',
              {
                timeline: [
                  '*',
                  {
                    lesson: ['*']
                  }
                ]
              }
            ]
          }
        ],
        filter,
        sort: ['-name']
      })
    )
  }

  getPublishedParenGameMapId(dictionaryId: string) {
    return this.directus.request(
      readItems('game_map', {
        fields: ['id'],
        filter: {
          parentmap: { _null: true },
          dictionary: { _eq: dictionaryId },
          status: { _eq: DictionaryStatus.Published }
        }
      })
    )
  }

  getGameMapById(mapId: string) {
    return this.directus.request(
      readItem('game_map', mapId, {
        fields: [
          { dictionary: ['id', 'name'] },
          'id',
          'name',
          'date_updated',
          'date_created',
          'status',
          {
            timeline: [
              '*',
              'level.*',
              {
                timeline: [
                  '*',
                  {
                    lesson: ['*', 'image_coloured.*', 'image_mono.*', 'sideActivities.*']
                  }
                ]
              }
            ]
          }
        ]
      })
    )
  }

  getGameMapLevelByLevelId(levelId: string) {
    return this.directus.request(
      readItems('game_map', {
        fields: ['id'],
        filter: {
          level: { _eq: levelId }
        }
      })
    )
  }

  createGameMap(gameMap: Partial<GameMap>) {
    return this.directus.request(createItem('game_map', gameMap))
  }

  createGameMaps(gameMaps: Partial<GameMap>[]) {
    return this.directus.request(createItems('game_map', gameMaps))
  }

  deleteGameMaps(mapIds: string[]) {
    return this.directus.request(deleteItems('game_map', mapIds))
  }

  updateGameMap(mapId: string, updatedMap: Partial<GameMap>) {
    return this.directus.request(updateItem('game_map', mapId, updatedMap))
  }

  updateGameMaps(mapIds: string[], updatedMap: Partial<GameMap>) {
    return this.directus.request(updateItems('game_map', mapIds, updatedMap))
  }

  createNewLevel(level: Partial<Level>) {
    return this.directus.request(createItem('level', level))
  }

  getLevelById(id: string) {
    return this.directus.request(readItem('level', id, { fields: ['*', 'image_coloured.*', 'image_mono.*'] }))
  }

  updateLevel(id: string, level: Partial<ManagementLevel> | Partial<Level>) {
    return this.directus.request(updateItem('level', id, level))
  }

  deleteLevels(ids: string[]) {
    return this.directus.request(deleteItems('level', ids))
  }

  deleteALesson(lessonId: string) {
    return this.directus.request(deleteItem('lesson', lessonId))
  }

  createNewLesson(lesson: Partial<Lesson>) {
    return this.directus.request(createItem('lesson', lesson))
  }

  createNewLessons(lessons: Partial<Lesson>[]) {
    return this.directus.request(createItems('lesson', lessons))
  }

  getLessonById(id: string) {
    return this.directus.request(readItem('lesson', id))
  }

  updateLesson(id: string, lesson: Partial<Lesson>) {
    return this.directus.request(updateItem('lesson', id, lesson))
  }

  createLessonSideActivities(lessonGame: Partial<LessonGame>) {
    return this.directus.request(createItem('lesson_game', lessonGame))
  }

  updateLessonSideActivities(id: number, lessonGame: Partial<LessonGame>) {
    return this.directus.request(updateItem('lesson_game', id, lessonGame))
  }

  createGameFiles(files: Partial<GameFiles>[]) {
    return this.directus.request(createItems('game_files', files))
  }

  updateGameFile(id: number, file: Partial<GameFiles>) {
    return this.directus.request(updateItem('game_files', id, file))
  }

  deleteLessons(ids: string[]) {
    return this.directus.request(deleteItems('lesson', ids))
  }

  getGameMapByDictionaryId(dictionaryIds: string[], status = GameMapStatus.Published) {
    return this.directus.request(
      readItems('game_map', {
        fields: ['id', 'dictionary'],
        filter: {
          dictionary: { _in: dictionaryIds },
          status: { _eq: status }
        },
        limit: -1
      })
    )
  }
  getLevelByDictionaryId(dictionaryId: string, version = GameMapStatus.Published) {
    return this.directus.request(
      readItems('level', {
        fields: ['id', 'dictionary'],
        filter: {
          dictionary: { _in: dictionaryId },
          version: { _eq: version }
        },
        limit: -1
      })
    )
  }
  //#endregion Game
}
